import { ObservableStore } from '@codewithdan/observable-store';
import { ReduxDevToolsExtension } from '@codewithdan/observable-store-extensions';
import RemoteNodeConnection from 'libs/remote_node_connection';
import * as updateMode from 'libs/update_mode';

const digestFilteredJobs = (jobs, jobFilter) => {
	if (!jobFilter) {
		return '';
	}
	
	return _.chain(jobs || [])
		.filter(jobFilter)
		.map((job) => {
			const progress = job.progress;
			return [job.id, progress?.state, progress?.message, JSON.stringify(progress?.progress || null), job.failedReason].join('\t');
		})
		.sort()
		.value()
		.join('\n');
};

const buildFilteredProperties = (state, propertyNames, filters) => {
	const currentProperties = {};
	propertyNames.forEach((propertyName) => {
		let value = state[propertyName];
		const filterFn = filters?.[propertyName];
		if (filterFn && Array.isArray(value)) {
			value = value.filter(filterFn);
		}
		currentProperties[propertyName] = value;
	});
	return currentProperties;
};

/** Same filtering as `subscribeToProperties` (for catch-up delivery when the first sync ran before callbacks were registered). */
const pickFilteredStoreSlice = (storeInstance, propertyNames, filters = {}) => {
	return buildFilteredProperties(storeInstance.getState() || {}, propertyNames, filters);
};

ObservableStore.globalSettings = {
	trackStateHistory: false,
	logStateChanges: false
};
ObservableStore.addExtension(new ReduxDevToolsExtension());

class Store extends ObservableStore {
	constructor(settings) {
		super(settings);
		const nodeId = new URL(document.baseURI).pathname.match(/^\/nodes\/([^/]+)\//)?.[1] ?? null;
		const fleetOnlyNamespaces = ['runtime', 'node'];
		const proxyable = !fleetOnlyNamespaces.includes(settings.namespace);
		const namespace = (nodeId && proxyable) ? `/fleet/${nodeId}/${settings.namespace}` : `/${settings.namespace}`;
		this.socket = new RemoteNodeConnection(namespace, {
			path: '/api',
			reconnection: true,
			reconnectionAttempts: Infinity,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 5000
		}, {
			nodeId: nodeId && proxyable ? nodeId : null,
			namespace: nodeId && proxyable ? `/${settings.namespace}` : null
		});
		this.namespace = settings.namespace;
		this.propertySubscribers = [];
		this.previousState = this.getState() || {};
		updateMode.registerStore(this);

		this.setState({ connected: false }, 'socket_init');

		this.socket.on('connect', () => {
			this.setState({ connected: true }, 'socket_connect');
		});

		this.socket.on('disconnect', () => {
			this.setState({ connected: false }, 'socket_disconnect');
		});

		this.globalStateWithPropertyChanges.subscribe((stateChange) => {
			if (stateChange === null) {
				return;
			}

			const newState = stateChange.state || {};
			const rawChanges = stateChange.stateChanges;
			const stateChanges =
				rawChanges && typeof rawChanges === 'object' && !Array.isArray(rawChanges) ? rawChanges : {};
			this.propertySubscribers.forEach((subscriber) => {
				const hasChanged = subscriber.properties.some((propertyName) => {
					return Object.prototype.hasOwnProperty.call(stateChanges, propertyName);
				});
				if (!hasChanged) {
					return;
				}

				const filters = subscriber.filters || {};
				const jobFilter = filters.jobs;
				if (jobFilter && subscriber.properties.includes('jobs')) {
					const changedSubscribedKeys = subscriber.properties.filter((propertyName) => {
						return Object.prototype.hasOwnProperty.call(stateChanges, propertyName);
					});
					const onlyJobsChangedAmongSubscribed =
						changedSubscribedKeys.length > 0
						&& changedSubscribedKeys.every((key) => key === 'jobs');
					if (onlyJobsChangedAmongSubscribed) {
						const prevDigest = digestFilteredJobs(this.previousState.jobs, jobFilter);
						const nextDigest = digestFilteredJobs(newState.jobs, jobFilter);
						if (prevDigest === nextDigest) {
							return;
						}
					}
				}

				subscriber.callback(buildFilteredProperties(newState, subscriber.properties, filters));
			});
			this.previousState = newState;
		});
	}

	setState(state, ...args) {
		if (updateMode.blocksStateWrite(this.namespace, _.keys(state))) {
			return this.getState();
		}

		return super.setState(state, ...args);
	}

	subscribeToProperties(propertyNames, callback, options = {}) {
		const filters = options.filters || {};
		this.propertySubscribers.push({
			properties: propertyNames,
			callback: callback,
			filters: filters,
		});

		const currentState = this.getState() || {};
		callback(buildFilteredProperties(currentState, propertyNames, filters));

		return () => {
			this.propertySubscribers = this.propertySubscribers.filter(
				(sub) => sub.callback !== callback
			);
		};
	}
}

export default Store;
export { pickFilteredStoreSlice };
